import React, { useMemo } from "react";
import { Form, Input, InputNumber, Select, Row as AntRow, Col, Typography } from "antd";
import { ReactComponent as InputValidIcon } from "../../assets/svg/input-valid-icon.svg";
import { ReactComponent as InputInvalidIcon } from "../../assets/svg/input-invalid-icon.svg";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { States } from "../../constant/states";
import { BLACK, GRAY_200, GRAY_400, GRAY_600, PRIMARY_100, PRIMARY_400 } from "../../constant/colors";
import { Section, SectionTitle } from "../../component/sharedComponents/settingsEntity/components";
import { formatPhoneNumberWhileTyping } from "../../utils/formaters";
import { createNpiValidator, createZipCodeValidator, createPhoneNumberValidator } from "../../utils/validators";

const { Option } = Select;

// The 9 fields a Site Admin may edit — must match the backend whitelist
// (api/constants/Provider.js FACILITY_SETTINGS_EDITABLE_FIELDS). Matching string is display-only.
export const FACILITY_EDITABLE_FIELDS = Object.freeze([
  "name",
  "npi",
  "taxId",
  "address",
  "suiteNumber",
  "city",
  "state",
  "zipCode",
  "phone"
]);

const StyledForm = styled(Form)`
  .ant-form-item-label > label {
    font-weight: 600;
    display: inline-flex;
    align-items: center;
  }

  /* Hide the default red asterisk on the left */
  .ant-form-item-label > label.ant-form-item-required:not(.ant-form-item-required-mark-optional)::before {
    display: none !important;
  }

  /* Add black asterisk on the right (house style, same as PhysicianForm) */
  .ant-form-item-label > label.ant-form-item-required:not(.ant-form-item-required-mark-optional)::after {
    display: inline-block !important;
    content: "*" !important;
    color: ${BLACK} !important;
    font-size: 14px !important;
  }

  input,
  .ant-select-selection-item {
    font-size: 16px;
  }

  /* Figma field: white, 1px Grey-200 border, 10px radius, 16px text */
  .ant-input,
  .ant-input-number {
    height: 40px;
    border-radius: 10px;
    border-color: ${GRAY_200};
  }

  /* A suffixed input (facility name live check) wraps the inner input in an affix wrapper;
     the height must live on the wrapper or the field ends up taller than its 40px siblings. */
  .ant-input-affix-wrapper {
    height: 40px;
    border-radius: 10px;
    border-color: ${GRAY_200};

    .ant-input {
      height: auto;
    }
  }

  /* TMCore field states (1078:20941): Grey 600 on hover, Primary 400 + 2px Primary 100 ring on focus.
     Focused/open fields are excluded from hover so typing with the pointer over the field keeps focus. */
  .ant-input:not(:disabled):not(:focus):hover,
  .ant-input-affix-wrapper:not(.ant-input-affix-wrapper-disabled):not(.ant-input-affix-wrapper-focused):hover,
  .ant-input-number:not(.ant-input-number-disabled):not(.ant-input-number-focused):hover,
  .ant-select:not(.ant-select-disabled):not(.ant-select-focused):not(.ant-select-open):hover .ant-select-selector {
    border-color: ${GRAY_600} !important;
  }

  .ant-input:focus,
  .ant-input-affix-wrapper-focused,
  .ant-input-number-focused,
  .ant-select-focused .ant-select-selector,
  .ant-select-open .ant-select-selector {
    border-color: ${PRIMARY_400} !important;
    box-shadow: 0 0 0 2px ${PRIMARY_100} !important;
  }

  /* the inner input of a suffixed field is borderless; the wrapper carries the focus ring */
  .ant-input-affix-wrapper > .ant-input:focus {
    border: none !important;
    box-shadow: none !important;
  }

  /* Placeholders: 16px regular Grey 400 (Figma), identical across inputs and the State select */
  .ant-input::placeholder,
  .ant-input-number-input::placeholder {
    font-size: 16px;
    font-weight: 400;
    color: ${GRAY_400};
  }

  .ant-select-selection-placeholder {
    font-size: 16px;
    font-weight: 400;
    color: ${GRAY_400};
  }

  .ant-input-number-input {
    height: 40px;
  }

  .ant-select-selector {
    height: 40px !important;
    border-radius: 10px !important;
    border-color: ${GRAY_200} !important;

    .ant-select-selection-item,
    .ant-select-selection-placeholder {
      line-height: 40px !important;
    }
  }

  /* antd pins the search input to 30px; stretch it to the 40px selector so the caret is centered */
  .ant-select-single .ant-select-selector .ant-select-selection-search-input {
    height: 38px;
    line-height: 38px;
  }

  .ant-select-arrow {
    color: ${BLACK};
  }
`;

const Row = styled(AntRow)`
  padding-right: 155px;
`;

// Matching string is display-only (Figma "Edit Closed" field): label + plain text, no input chrome.
const ReadOnlyValue = styled(Typography.Text)`
  display: block;
  font-size: 16px;
  line-height: 40px;
  color: ${BLACK};
`;

export const FacilityForm = ({
  form,
  initialValues,
  matchingStrings,
  nameCheck,
  onFieldsChange,
  onValuesChange,
  onNameChange
}) => {
  const { t } = useTranslation();

  const { validateNPI, validateZipCode, validatePhoneNumber } = useMemo(
    () => ({
      validateNPI: createNpiValidator(
        t("facility_management.form.validation_npi_required"),
        t("facility_management.form.validation_npi_format")
      ),
      validateZipCode: createZipCodeValidator(t("facility_management.form.validation_zip_format")),
      validatePhoneNumber: createPhoneNumberValidator(t("facility_management.form.phone_enter_digits"))
    }),
    [t]
  );

  // Live uniqueness feedback (same UX as the MRN field on Create New Patient):
  // checking → spinner, unique → green check, taken → red icon + message.
  const isNameTaken = nameCheck?.status === "taken";
  const nameValidateStatus =
    nameCheck?.status === "checking"
      ? "validating"
      : isNameTaken
      ? "error"
      : nameCheck?.status === "unique"
      ? "success"
      : undefined;
  const nameHelp = isNameTaken ? (
    <span id="Facility_Name_Taken_Error">
      {nameCheck.message || t("facility_management.form.validation_name_taken")}
    </span>
  ) : undefined;
  const nameSuffix =
    nameCheck?.status === "unique" ? <InputValidIcon /> : isNameTaken ? <InputInvalidIcon /> : <span />;

  return (
    <StyledForm
      form={form}
      layout="vertical"
      initialValues={initialValues}
      onFieldsChange={onFieldsChange}
      onValuesChange={onValuesChange}
      requiredMark
      id="Facility_Form"
    >
      <Section>
        <SectionTitle>{t("facility_management.detail.section_main")}</SectionTitle>
        <Row gutter={16}>
          <Col span={6}>
            <Form.Item
              name="name"
              label={t("facility_management.detail.label_name")}
              rules={[
                { required: true, whitespace: true, message: t("facility_management.form.validation_name_required") }
              ]}
              hasFeedback={nameCheck?.status === "checking"}
              validateStatus={nameValidateStatus}
              help={nameHelp}
            >
              <Input
                id="Facility_Form_Name"
                placeholder={t("facility_management.form.placeholder_name")}
                onChange={(e) => onNameChange && onNameChange(e.target.value)}
                suffix={nameSuffix}
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item label={t("facility_management.detail.label_matching_string")}>
              <ReadOnlyValue
                id="Facility_Form_MatchingString"
                title={t("facility_management.form.matching_string_readonly_hint")}
                ellipsis={{ tooltip: matchingStrings }}
              >
                {matchingStrings || "-"}
              </ReadOnlyValue>
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              name="npi"
              label={t("facility_management.detail.label_npi")}
              rules={[{ required: true, validator: validateNPI }]}
            >
              <Input
                id="Facility_Form_Npi"
                placeholder={t("facility_management.form.placeholder_npi")}
                maxLength={10}
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="taxId" label={t("facility_management.detail.label_tax_id")}>
              <Input id="Facility_Form_TaxId" placeholder={t("facility_management.form.placeholder_tax_id")} />
            </Form.Item>
          </Col>
        </Row>
      </Section>

      <Section>
        <SectionTitle>{t("facility_management.detail.section_location")}</SectionTitle>
        <Row gutter={16}>
          <Col span={6}>
            <Form.Item name="address" label={t("facility_management.detail.label_address")}>
              <Input id="Facility_Form_Address" placeholder={t("facility_management.form.placeholder_address")} />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="suiteNumber" label={t("facility_management.detail.label_suite")}>
              <Input id="Facility_Form_Suite" placeholder={t("facility_management.form.placeholder_suite")} />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="city" label={t("facility_management.detail.label_city")}>
              <Input id="Facility_Form_City" placeholder={t("facility_management.form.placeholder_city")} />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="state" label={t("facility_management.detail.label_state")}>
              <Select
                id="Facility_Form_State"
                showSearch
                allowClear
                placeholder={t("facility_management.form.placeholder_state")}
                filterOption={(input, option) => option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
              >
                {Object.values(States).map((state) => (
                  <Option key={state.code} value={state.code} className="state-option">
                    {state.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={6}>
            <Form.Item
              name="zipCode"
              label={t("facility_management.detail.label_zip")}
              rules={[{ validator: validateZipCode }]}
            >
              <Input id="Facility_Form_Zip" placeholder={t("facility_management.form.placeholder_zip")} maxLength={5} />
            </Form.Item>
          </Col>
        </Row>
      </Section>

      <Section>
        <SectionTitle>{t("facility_management.detail.section_contact")}</SectionTitle>
        <Row gutter={16}>
          <Col span={6}>
            <Form.Item
              name="phone"
              label={t("facility_management.detail.label_phone")}
              rules={[{ validator: validatePhoneNumber }]}
            >
              <InputNumber
                id="Facility_Form_Phone"
                maxLength={14}
                controls={false}
                placeholder={t("facility_management.form.phone_pattern")}
                formatter={(value) => formatPhoneNumberWhileTyping(value)}
                parser={(value) => value.replace(/[^0-9]/g, "")}
                style={{ width: "100%" }}
              />
            </Form.Item>
          </Col>
        </Row>
      </Section>
    </StyledForm>
  );
};

export default FacilityForm;
